import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import PaymentModal from "../../Modals/PaymentModal";
import TapPayment from "../../payment/TapPayment";
import {
  useAdminAddPaymentMethodMutation,
} from "../../../services/organization/subscription";
import { toast } from "react-toastify";

const AddNewCardModal = ({ isOpened, onClose }) => {
  const { t, i18n } = useTranslation("subscription");
  const tapRef = useRef(null);
  const [addPaymentMethod, { isLoading }] = useAdminAddPaymentMethodMutation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [tapError, setTapError] = useState(null);

  const isBusy = isLoading || isRedirecting;

  const handleClose = () => {
    setTapError(null);
    onClose();
  };

  const handleAddCard = async () => {
    if (isBusy || !tapRef.current) return;
    setTapError(null);

    try {
      const tokenId = await tapRef.current.tokenize();
      setIsRedirecting(true);

      const response = await addPaymentMethod({ card_token: tokenId }).unwrap();
      const redirectUrl = response?.data?.redirect_url || response?.redirect_url;

      if (!redirectUrl) {
        throw new Error("Missing redirect URL from server");
      }

      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Add card failed:", error);
      setIsRedirecting(false);
      const message =
        error?.data?.message ||
        error?.message ||
        t("paymentMethods.addFailed", "Failed to add card. Please try again.");
      setTapError(message);
      toast.error(message);
    }
  };

  const handleTokenizationError = () => {
    setTapError(
      t(
        "paymentMethods.tokenizationFailed",
        "We couldn't process your card details. Please check the information and try again.",
      ),
    );
  };

  return (
    <PaymentModal
      id="AddNewCardModal"
      isOpened={isOpened}
      onClose={handleClose}
      title={t("paymentMethods.addNewCard", "Add New Card")}
      subtitle={t(
        "paymentMethods.addNewCardSubtitle",
        "Enter your payment information to securely add a new card.",
      )}
      primaryLabel={
        isBusy
          ? t("paymentMethods.processing", "Processing...")
          : t("paymentMethods.addCard", "Add Card")
      }
      onPrimary={handleAddCard}
      isPrimaryLoading={isBusy}
      cancelLabel={t("common:cancel", "Cancel")}
      onCancel={handleClose}
      maxWidth={520}
    >
      {/* Tap Payments SDK Card Form */}
      <TapPayment
        ref={tapRef}
        hideHeader
        onTokenizationError={handleTokenizationError}
        onReady={() => setTapError(null)}
        customer={{}}
        transaction={{ amount: 1, currency: "KWD" }}
        interfaceConfig={{
          locale: i18n.language === "ar" ? "ar" : "en",
          direction: i18n.language === "ar" ? "rtl" : "ltr",
          theme: "light",
        }}
      >
        {tapError && (
          <div className="payment-modal__error-banner" role="alert">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <span>{tapError}</span>
          </div>
        )}
      </TapPayment>

      {/* Security note */}
      {/* <p className="payment-modal__security-note">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
        </svg>
        {t(
          "paymentMethods.securityNote",
          "Your payment information is encrypted and secure.",
        )}
      </p> */}
    </PaymentModal>
  );
};

AddNewCardModal.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddNewCardModal;
